// Shared helpers for the 30-minute-slot combobox time pickers
// (time-select on onboarding, time-input combobox mode on the event form).
//
// All times are 12-hour "h:i K" strings ("1:00 PM"). Two label styles:
//   words = true  → 12:00 AM / 12:00 PM render as "Midnight" / "Noon"
//   words = false → plain "12:00 AM" / "12:00 PM"

// Format minutes-since-midnight → label.
export function formatSlot(totalMinutes, { words = false } = {}) {
  if (words && totalMinutes === 0) return "Midnight";
  if (words && totalMinutes === 720) return "Noon";
  let h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const period = h < 12 ? "AM" : "PM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${period}`;
}

// The list of 30-minute slot labels.
//   startOfDay → drop midnight, lead with 12:01 AM
//   endOfDay   → drop midnight, finish with 11:59 PM
export function slotLabels({ words = false, startOfDay = false, endOfDay = false } = {}) {
  const mins = [];
  if (startOfDay) mins.push(1); // 12:01 AM
  const first = startOfDay || endOfDay ? 30 : 0; // skip midnight for the boundary variants
  for (let m = first; m < 24 * 60; m += 30) mins.push(m);
  if (endOfDay) mins.push(24 * 60 - 1); // 11:59 PM
  return mins.map((m) => formatSlot(m, { words }));
}

// Normalize a freely-typed value to a canonical label, or null if unparseable.
// Accepts "10", "10:30", "10pm", "10:30 PM", "22:00", and (when words) "midnight"/"noon".
export function normalizeTime(raw, { words = false } = {}) {
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  if (words && s === "midnight") return formatSlot(0, { words });
  if (words && s === "noon") return formatSlot(720, { words });
  const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const period = m[3];
  if (h > 23 || min > 59) return null;
  if (period) {
    if (h < 1 || h > 12) return null;
    if (period === "pm" && h !== 12) h += 12;
    if (period === "am" && h === 12) h = 0;
  }
  // No am/pm and h in 0–23 → treat as 24-hour.
  return formatSlot(h * 60 + min, { words });
}

// A label → minutes-since-midnight, or null. Understands "Midnight"/"Noon".
export function minutesFromLabel(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  if (s === "midnight") return 0;
  if (s === "noon") return 720;
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (m[3] === "pm" && h !== 12) h += 12;
  if (m[3] === "am" && h === 12) h = 0;
  return h * 60 + min;
}

// Characters allowed while typing (digits, colon, space, letters for am/pm and
// midnight/noon). Blur-normalization is the real gate.
export const ALLOWED_TIME_CHARS = /^[0-9:\sa-zA-Z]+$/;
